'use client';

import { useAtom } from 'jotai';
import { defineMessage, useIntl } from 'react-intl';

import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useSearchQuery } from '../../../useSearchQuery';
import { isMobileSearchVisibleAtom } from '../../../Header.atoms';
import { SEARCH_SLOT_ID } from '../../../Header.ids';
import type { SlotPriority } from '../Base/types';

const title = defineMessage({
  id: 'slot.search.label',
  defaultMessage: 'Suche',
});

/** Search slot is only displayed on mobile devices and shows the searchfield in the popover */
export const SearchSlot = ({ priority }: { priority: SlotPriority }) => {
  const { formatMessage } = useIntl();
  const searchQuery = useSearchQuery();
  const { isHydrated } = useIsHydrated();
  const isMobileSearchAlwaysVisible = !!searchQuery; // we are on a SERP if a query exists

  const [isMobileSearchVisible, setIsMobileSearchVisible] = useAtom(isMobileSearchVisibleAtom);

  const pathname = usePathname();
  const oldPathname = useRef(pathname);

  // close the search field when navigating to a new page
  useEffect(() => {
    if (oldPathname.current !== pathname) {
      setIsMobileSearchVisible(false);
      oldPathname.current = pathname;
    }
  }, [pathname, setIsMobileSearchVisible]);

  return (
    <button
      // for checks in ResponsiveNavigation.SearchWrapper, required for click capturing logic on click-away
      id={SEARCH_SLOT_ID}
      type="button"
      onClick={() => {
        // never toggle on SERP, keep the search field always visible
        // toggle it for a frame to trigger auto-focus, enabling easier navigation with the keyboard or mouse directly to the input
        if (isMobileSearchAlwaysVisible) {
          setIsMobileSearchVisible(true);
          setTimeout(() => {
            setIsMobileSearchVisible(false);
          });
        }

        setIsMobileSearchVisible(!isMobileSearchVisible);
      }}
      // small triangle to indicate the connection between the search slot button and the search input with pseudo classes
      className={`slot-priority-${priority} relative flex flex-col items-center py-0.5 lg:hidden ${isHydrated ? 'cursor-pointer' : 'cursor-wait'} after:border-color-transparent after:absolute after:bottom-0 after:-mb-1 after:h-0 after:w-0 after:border-[10px] ${(isMobileSearchVisible || isMobileSearchAlwaysVisible) && 'after:border-b-color-grey-light'}`}
      aria-label={formatMessage(title)}
      aria-disabled={!isHydrated}
      disabled={!isHydrated}
    >
      <div
        className={`icon-emp-search ${(isMobileSearchVisible || isMobileSearchAlwaysVisible) && 'text-color-primary'} h-4 w-4`}
      />
    </button>
  );
};
